import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  TextField,
  Button,
  Box,
  Snackbar,
  Alert,
  Container,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Chip,
  Paper,
  Badge,
  Stack,
  DialogContent,
  DialogActions,
  Dialog,
  DialogTitle,
} from "@mui/material";
import { PriorityHigh, Close } from "@mui/icons-material";
import {
  fetchProducts,
  fetchGas,
  resetGas,
  createTransaction,
} from "../utils/api";
import { getUser } from "../utils/auth";
import handlePrint from "../utils/print";

const TransactionForm = () => {
  const user = getUser();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm();
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [products, setProducts] = useState([]);
  const [detergents, setDetergents] = useState([]);
  const [fabcons, setFabcons] = useState([]);
  const [bleaches, setBleaches] = useState([]);
  const [gas, setGas] = useState(0);
  const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
  const [registerDialog, setRegisterDialog] = useState(null);

  const handleOpenRegisterDialog = () => setRegisterDialogOpen(true);
  const handleCloseRegisterDialog = () => setRegisterDialogOpen(false);

  const selectedDtId = watch("dt", "");
  const dtQty = watch("dtQty", 0);
  const dt = detergents.find((product) => product._id === selectedDtId);

  const dtSubTotal = (dtQty || 0) * (dt?.price || 0);

  const selectedFcId = watch("fc", "");
  const fcQty = watch("fcQty", 0);
  const fc = fabcons.find((product) => product._id === selectedFcId);

  const fcSubTotal = (fcQty || 0) * (fc?.price || 0);

  const selectedBlId = watch("bl", "");
  const blQty = watch("blQty", 0);
  const bl = bleaches.find((product) => product._id === selectedBlId);

  const blSubTotal = (blQty || 0) * (bl?.price || 0);

  const totalPrice =
    (watch("ws") || 0) +
    (watch("dr") || 0) +
    (watch("fd") || 0) +
    dtSubTotal +
    fcSubTotal +
    blSubTotal +
    (watch("sp") || 0) +
    (watch("ad") || 0) +
    (watch("sw") || 0);

  const change = (watch("cs") || 0) - totalPrice;

  useEffect(() => {
    if (!selectedDtId) {
      setValue("dtQty", "");
    } else {
      setValue("dtQty", 1);
    }
  }, [selectedDtId]);

  useEffect(() => {
    if (!selectedFcId) {
      setValue("fcQty", "");
    } else {
      setValue("fcQty", 1);
    }
  }, [selectedFcId]);

  useEffect(() => {
    if (!selectedBlId) {
      setValue("blQty", "");
    } else {
      setValue("blQty", 1);
    }
  }, [selectedBlId]);

  useEffect(() => {
    if (totalPrice && change >= 0) {
      setIsButtonDisabled(false);
    } else {
      setIsButtonDisabled(true);
    }
  }, [totalPrice, change]);

  useEffect(() => {
    fetchProductsData();
    fetchGasData();
  }, []);

  // useEffect(() => {
  //   console.log("ggg");
  //   if (gas <= 10) {
  //     showGasWarning();
  //   }
  // }, [gas]);

  const sanitizeNumberInput = (input, max = 99999) => {
    // Remove all non-numeric characters, except for the decimal point
    let sanitizedInput = input.replace(/[^0-9]/g, "");

    // Convert to a number
    let numericValue = parseInt(sanitizedInput, 10);

    // If the value is NaN (i.e., input was empty or no numeric value), set to 0
    if (isNaN(numericValue)) {
      numericValue = 0;
    }

    // Ensure the value doesn't exceed 99999
    if (numericValue > max) {
      numericValue = max;
    }

    return numericValue;
  };

  const sanitizeFloatInput = (input) => {
    // Remove all non-numeric characters except for the first dot
    let sanitizedInput = input.replace(/[^0-9.]/g, "");

    // Ensure only one dot is allowed; remove any subsequent dots
    const dotIndex = sanitizedInput.indexOf(".");
    if (dotIndex !== -1) {
      sanitizedInput =
        sanitizedInput.slice(0, dotIndex + 1) +
        sanitizedInput.slice(dotIndex + 1).replace(/\./g, "");
    }

    // Limit the length to 5 characters
    if (sanitizedInput.length > 5) {
      sanitizedInput = sanitizedInput.slice(0, 5);
    }

    return sanitizedInput;
  };

  const handleReset = () => {
    reset();
  };

  const fetchProductsData = async () => {
    try {
      const data = await fetchProducts({ branch: user.br });
      const detergents = data.data.filter((product) => product.cat === 1);
      const fabcons = data.data.filter((product) => product.cat === 2);
      const bleaches = data.data.filter((product) => product.cat === 3);
      setProducts(data.data);
      setDetergents(detergents);
      setFabcons(fabcons);
      setBleaches(bleaches);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
    }
  };

  const fetchGasData = async () => {
    try {
      const data = await fetchGas({ branch: user.br });
      setGas(data.data?.gas);
    } catch (error) {
      console.error("Error fetching gas:", error);
    } finally {
    }
  };

  const onSubmit = async (data) => {
    let receipt = "";
    try {
      setIsButtonDisabled(true);
      data.dtPrice = dt?.price || 0;
      data.fcPrice = fc?.price || 0;
      data.blPrice = bl?.price || 0;
      const response = await createTransaction(data);
      if (response.status === 201) {
        receipt = response.data.data;
        setSnackbarMessage("Transaction saved successfully!");
        setOpenSnackbar(true);
        reset();
        fetchProductsData();
        if (data?.dr || data?.ad) {
          fetchGasData();
        }
      }
    } catch (error) {
      console.error("Error saving transaction:", error);
      setSnackbarMessage("Failed to save transaction.");
      setOpenSnackbar(true);
    }

    if (receipt) {
      handlePrint(receipt, 2);
    }
  };

  const connectAndPrint = async () => {
    try {
      // Request the Bluetooth device
      const device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true, // Accept any device
        optionalServices: ["49535343-fe7d-4ae5-8fa9-9fafd205e455"], // We'll discover services dynamically
      });

      console.log("Selected device:", device.name);
      console.log("Selected device:", device);

      // Connect to the device's GATT server
      const server = await device.gatt.connect();

      // Discover services
      const services = await server.getPrimaryServices();
      console.log(`Service: ${services}`);

      // Loop through services to find available characteristics
      for (const service of services) {
        console.log(`Service UUID: ${service.uuid}`);
        const characteristics = await service.getCharacteristics();

        for (const characteristic of characteristics) {
          console.log(`Characteristic UUID: ${characteristic.uuid}`);
          console.log("Supports Write:", characteristic.properties.write);

          // Check if the characteristic supports writing
          if (characteristic.properties.write) {
            console.log("Found a write characteristic!");
            // You can now proceed to send data to this characteristic
            const encoder = new TextEncoder();
            const data = encoder.encode("Hello, Printer!");
            await characteristic.writeValue(data);
            console.log("Data sent to the printer!");
          }
        }
      }

      alert("Services and characteristics discovered. Check the console.");
    } catch (error) {
      console.error("Error connecting to printer:", error);
    }
  };

  const connectAndPrintx = async () => {
    try {
      // Request the Bluetooth device
      const device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true, // Accept any device
        optionalServices: ["49535343-fe7d-4ae5-8fa9-9fafd205e455"], // We'll discover services dynamically
      });

      // Connect to the device's GATT server
      const server = await device.gatt.connect();

      // Discover services
      const service = await server.getPrimaryServices(
        "49535343-fe7d-4ae5-8fa9-9fafd205e455"
      );

      const characteristic = await service.getCharacteristic(
        "49535343-8841-43f4-a8d4-ecbe34729bb3"
      );

      const encoder = new TextEncoder();
      const data = encoder.encode("Hello, Printer!");
      await characteristic.writeValue(data);
      console.log("Data sent to the printer!");
    } catch (error) {
      console.error("Error connecting to printer:", error);
    }
  };

  const connectAndPrint2 = async () => {
    try {
      // Request the Bluetooth device
      const device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true, // Accept any device
        optionalServices: ["f0380176-2f64-077d-3642-163ef43f7ddb"], // We'll discover services dynamically
      });

      console.log("Selected device:", device.name);
      console.log("Selected device:", device);

      // Connect to the device's GATT server
      const server = await device.gatt.connect();

      // Discover services
      const services = await server.getPrimaryServices();
      console.log(`Service: ${services}`);

      // Loop through services to find available characteristics
      for (const service of services) {
        console.log(`Service UUID: ${service.uuid}`);
        const characteristics = await service.getCharacteristics();

        for (const characteristic of characteristics) {
          console.log(`Characteristic UUID: ${characteristic.uuid}`);
          console.log("Supports Write:", characteristic.properties.write);

          // Check if the characteristic supports writing
          if (characteristic.properties.write) {
            console.log("Found a write characteristic!");
            // You can now proceed to send data to this characteristic
            const encoder = new TextEncoder();
            const data = encoder.encode("Hello, Printer!");
            await characteristic.writeValue(data);
            console.log("Data sent to the printer!");
          }
        }
      }

      alert("Services and characteristics discovered. Check the console.");
    } catch (error) {
      console.error("Error connecting to printer:", error);
      alert(error);
    }
  };

  const showGasWarning = () => {
    setRegisterDialog(
      <>
        <DialogTitle id="alert-dialog-title">
          Almost time to reset the gas!
        </DialogTitle>
        <DialogContent>
          Check if the gas needs reset already. If so, kindly click the RESET
          button after.
          <br />
          <br />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRegisterDialog}>Close</Button>
        </DialogActions>
      </>
    );

    handleOpenRegisterDialog();
  };

  const handleResetGasConfirm = () => {
    setRegisterDialog(
      <>
        <DialogTitle id="alert-dialog-title">
          Are you sure you want to reset the gas?
        </DialogTitle>
        <DialogContent>
          This action will reset the gas to 0.
          <br />
          <br />
          <Typography variant="caption">
            If you encountered an issue, please try again later.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRegisterDialog}>Cancel</Button>
          <Button onClick={() => handleResetGas()} autoFocus>
            Continue
          </Button>
        </DialogActions>
      </>
    );

    handleOpenRegisterDialog();
  };

  const handleResetGas = async () => {
    try {
      const response = await resetGas({ branch: user.br });
      if (response.status === 200) {
        setSnackbarMessage("Gas was reset successfully!");
        setOpenSnackbar(true);
        handleCloseRegisterDialog();
        setGas(0); // fetchGasData();
      } else {
        setSnackbarMessage("Failed resetting gas!");
        setOpenSnackbar(true);
      }
    } catch (error) {
      console.error("Error:", error);
      setSnackbarMessage("Failed resetting gas!");
      setOpenSnackbar(true);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ padding: 2 }}>
      <Box
        overflow="auto"
        sx={{
          border: 0,
          position: "relative",
          display: "flex",
          gap: 1,
          padding: 0,
          marginBottom: 1,
        }}
      >
        <Paper
          variant="outlined"
          sx={{ padding: 1, paddingY: 0, minWidth: "130px" }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="caption">GAS Usage</Typography>
            <Typography variant="body6" sx={{ fontWeight: "bold" }}>
              {gas}
            </Typography>
          </Box>
          <Button
            // disabled={gas > 10}
            tabIndex={-1}
            variant="contained"
            size="small"
            sx={{ fontSize: 12, padding: 0, marginBottom: 1 }}
            onClick={handleResetGasConfirm}
          >
            Reset
          </Button>{" "}
          <Typography
            variant="caption"
            color={
              gas >= 230
                ? gas >= 220
                  ? "error.main"
                  : "warning.main"
                : "text.primary"
            }
          >
            {gas >= 220 ? "Low gas" : ""}
          </Typography>
        </Paper>
        {products.map((product, index) => (
          <Paper
            variant="outlined"
            key={index}
            sx={{ padding: 1, paddingY: 0, minWidth: "130px" }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="caption">{product.nm}</Typography>
              <Typography variant="body6" sx={{ fontWeight: "bold" }}>
                {product.stocks || 0}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.disabled">
              {product.cat === 1
                ? "Detergent"
                : product.cat === 2
                ? "Fabcon"
                : product.cat === 3
                ? "Bleach"
                : ""}
            </Typography>{" "}
            <Typography
              variant="caption"
              color={
                (product.stocks || 0) <= 5
                  ? (product.stocks || 0) === 0
                    ? "error.main"
                    : "warning.main"
                  : "text.primary"
              }
            >
              {(product.stocks || 0) <= 5
                ? (product.stocks || 0) === 0
                  ? "Out of stock"
                  : "Low stock"
                : ""}
            </Typography>
          </Paper>
        ))}
      </Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ marginBottom: 1 }}
        maxWidth="sm"
      >
        <Typography variant="h5">New Transaction</Typography>
        {/* <Button
          type="button"
          variant="outlined"
          onClick={() => connectAndPrint2()}
        >
          Test print
        </Button> */}
        <Button type="button" variant="outlined" onClick={() => handleReset()}>
          Clear
        </Button>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={1} maxWidth="sm">
          <TextField
            label="Customer Name"
            fullWidth
            variant="outlined"
            size="small"
            {...register("nm", { required: "Customer name is required" })}
            value={(watch("nm") || "").slice(0, 50)}
            error={!!errors.nm}
            helperText={errors.nm ? errors.nm.message : ""}
            autoComplete="off"
          />
          <TextField
            label="Weight (kg)"
            fullWidth
            variant="outlined"
            size="small"
            type="number"
            {...register("wt", {
              required: "Weight is required",
              valueAsNumber: true,
            })}
            onChange={(e) => {
              setValue("wt", sanitizeFloatInput(e.target.value));
            }}
            onKeyDown={(e) => {
              if (e.key === "e" || e.key === "+" || e.key === "-") {
                e.preventDefault();
              }
            }}
            value={watch("wt") || ""}
            error={!!errors.wt}
            helperText={errors.wt ? errors.wt.message : ""}
          />
          <TextField
            label="Wash Price"
            fullWidth
            variant="outlined"
            size="small"
            type="number"
            {...register("ws", {
              valueAsNumber: true,
            })}
            onChange={(e) => {
              setValue("ws", sanitizeNumberInput(e.target.value));
            }}
            onKeyDown={(e) => {
              if (
                e.key === "e" ||
                e.key === "+" ||
                e.key === "-" ||
                e.key === "."
              ) {
                e.preventDefault();
              }
            }}
            value={watch("ws") || ""}
            error={!!errors.ws}
            helperText={errors.ws ? errors.ws.message : ""}
          />
          <TextField
            label="Super Wash Price"
            fullWidth
            variant="outlined"
            size="small"
            type="number"
            {...register("sw", {
              valueAsNumber: true,
            })}
            onChange={(e) => {
              setValue("sw", sanitizeNumberInput(e.target.value));
            }}
            onKeyDown={(e) => {
              if (
                e.key === "e" ||
                e.key === "+" ||
                e.key === "-" ||
                e.key === "."
              ) {
                e.preventDefault();
              }
            }}
            value={watch("sw") || ""}
            error={!!errors.sw}
            helperText={errors.sw ? errors.sw.message : ""}
          />
          <TextField
            label="Spin Price"
            fullWidth
            variant="outlined"
            size="small"
            type="number"
            {...register("sp", {
              valueAsNumber: true,
            })}
            onChange={(e) => {
              setValue("sp", sanitizeNumberInput(e.target.value));
            }}
            onKeyDown={(e) => {
              if (
                e.key === "e" ||
                e.key === "+" ||
                e.key === "-" ||
                e.key === "."
              ) {
                e.preventDefault();
              }
            }}
            value={watch("sp") || ""}
            error={!!errors.sp}
            helperText={errors.sp ? errors.sp.message : ""}
          />
          <TextField
            label="Dry Price"
            fullWidth
            variant="outlined"
            size="small"
            type="number"
            {...register("dr", {
              valueAsNumber: true,
            })}
            onChange={(e) => {
              setValue("dr", sanitizeNumberInput(e.target.value));
            }}
            onKeyDown={(e) => {
              if (
                e.key === "e" ||
                e.key === "+" ||
                e.key === "-" ||
                e.key === "."
              ) {
                e.preventDefault();
              }
            }}
            value={watch("dr") || ""}
            error={!!errors.dr}
            helperText={errors.dr ? errors.dr.message : ""}
          />
          <TextField
            label="Add Dry Price"
            fullWidth
            variant="outlined"
            size="small"
            type="number"
            {...register("ad", {
              valueAsNumber: true,
            })}
            onChange={(e) => {
              setValue("ad", sanitizeNumberInput(e.target.value));
            }}
            onKeyDown={(e) => {
              if (
                e.key === "e" ||
                e.key === "+" ||
                e.key === "-" ||
                e.key === "."
              ) {
                e.preventDefault();
              }
            }}
            value={watch("ad") || ""}
            error={!!errors.ad}
            helperText={errors.ad ? errors.ad.message : ""}
          />
          <TextField
            label="Fold Price"
            fullWidth
            variant="outlined"
            size="small"
            type="number"
            {...register("fd", {
              valueAsNumber: true,
            })}
            onChange={(e) => {
              setValue("fd", sanitizeNumberInput(e.target.value));
            }}
            onKeyDown={(e) => {
              if (
                e.key === "e" ||
                e.key === "+" ||
                e.key === "-" ||
                e.key === "."
              ) {
                e.preventDefault();
              }
            }}
            value={watch("fd") || ""}
            error={!!errors.fd}
            helperText={errors.fd ? errors.fd.message : ""}
          />
          <div style={{ display: "flex", gap: "1rem" }}>
            <FormControl
              fullWidth
              sx={{ marginBottom: 1, width: "50%" }}
              size="small"
            >
              <InputLabel id="dt-select-label">Detergent</InputLabel>
              <Select
                labelId="dt-select-label"
                id="dt-select"
                label="Detergent"
                variant="outlined"
                {...register("dt")}
                value={watch("dt") || ""}
              >
                <MenuItem value="">
                  <em>-</em>
                </MenuItem>
                {detergents.map((product) => {
                  if (product.stocks > 0) {
                    return (
                      <MenuItem key={product._id} value={product._id}>
                        {product.nm + " (P" + product.price + ")"}
                      </MenuItem>
                    );
                  }
                })}
              </Select>
              <FormHelperText sx={{ margin: 0 }}>
                {errors.dt ? errors.dt.message : ""}
              </FormHelperText>
            </FormControl>
            <TextField
              fullWidth
              label="Quantity"
              variant="outlined"
              size="small"
              sx={{ marginBottom: 1, width: "25%" }}
              type="number"
              {...register("dtQty", {
                valueAsNumber: true,
                required: !!watch("dt"),
              })}
              onChange={(e) => {
                setValue(
                  "dtQty",
                  sanitizeNumberInput(e.target.value, dt?.stocks || 0)
                );
              }}
              onKeyDown={(e) => {
                if (
                  e.key === "e" ||
                  e.key === "+" ||
                  e.key === "-" ||
                  e.key === "."
                ) {
                  e.preventDefault();
                }
              }}
              value={watch("dtQty") || ""}
              disabled={!watch("dt")}
              error={!!errors.dtQty}
              helperText={errors.dtQty ? errors.dtQty.message : ""}
            />
            <Typography
              variant="body1"
              sx={{ marginBottom: 1, width: "25%", paddingY: "8px" }}
            >
              P {dtSubTotal}
            </Typography>
          </div>
          <div style={{ display: "flex", gap: "1rem", margin: 0 }}>
            <FormControl
              fullWidth
              sx={{ marginBottom: 1, width: "50%" }}
              size="small"
            >
              <InputLabel id="fc-select-label">Fabric Conditioner</InputLabel>
              <Select
                labelId="fc-select-label"
                id="fc-select"
                label="Fabric Conditioner"
                variant="outlined"
                {...register("fc")}
                value={watch("fc") || ""}
              >
                <MenuItem value="">
                  <em>-</em>
                </MenuItem>
                {fabcons.map((product) => {
                  if (product.stocks > 0) {
                    return (
                      <MenuItem key={product._id} value={product._id}>
                        {product.nm + " (P" + product.price + ")"}
                      </MenuItem>
                    );
                  }
                })}
              </Select>
              <FormHelperText sx={{ margin: 0 }}>
                {errors.fc ? errors.fc.message : ""}
              </FormHelperText>
            </FormControl>
            <TextField
              fullWidth
              label="Quantity"
              variant="outlined"
              size="small"
              sx={{ marginBottom: 1, width: "25%" }}
              type="number"
              {...register("fcQty", {
                valueAsNumber: true,
                required: !!watch("fc"),
              })}
              onChange={(e) => {
                setValue(
                  "fcQty",
                  sanitizeNumberInput(e.target.value, fc?.stocks || 0)
                );
              }}
              onKeyDown={(e) => {
                if (
                  e.key === "e" ||
                  e.key === "+" ||
                  e.key === "-" ||
                  e.key === "."
                ) {
                  e.preventDefault();
                }
              }}
              value={watch("fcQty") || ""}
              disabled={!watch("fc")}
              error={!!errors.fcQty}
              helperText={errors.fcQty ? errors.fcQty.message : ""}
            />
            <Typography
              variant="body1"
              sx={{ marginBottom: 1, width: "25%", paddingY: "8px" }}
            >
              P {fcSubTotal}
            </Typography>
          </div>
          <div style={{ display: "flex", gap: "1rem", margin: 0 }}>
            <FormControl fullWidth sx={{ width: "50%" }} size="small">
              <InputLabel id="bl-select-label">Bleach</InputLabel>
              <Select
                labelId="bl-select-label"
                id="bl-select"
                label="Bleach"
                variant="outlined"
                {...register("bl")}
                value={watch("bl") || ""}
              >
                <MenuItem value="">
                  <em>-</em>
                </MenuItem>
                {bleaches.map((product) => {
                  if (product.stocks > 0) {
                    return (
                      <MenuItem key={product._id} value={product._id}>
                        {product.nm + " (P" + product.price + ")"}
                      </MenuItem>
                    );
                  }
                })}
              </Select>
              <FormHelperText sx={{ margin: 0 }}>
                {errors.bl ? errors.bl.message : ""}
              </FormHelperText>
            </FormControl>
            <TextField
              fullWidth
              label="Quantity"
              variant="outlined"
              size="small"
              sx={{ marginBottom: 0, width: "25%" }}
              type="number"
              {...register("blQty", {
                valueAsNumber: true,
                required: !!watch("bl"),
              })}
              onChange={(e) => {
                setValue(
                  "blQty",
                  sanitizeNumberInput(e.target.value, bl?.stocks || 0)
                );
              }}
              onKeyDown={(e) => {
                if (
                  e.key === "e" ||
                  e.key === "+" ||
                  e.key === "-" ||
                  e.key === "."
                ) {
                  e.preventDefault();
                }
              }}
              value={watch("blQty") || ""}
              disabled={!watch("bl")}
              error={!!errors.blQty}
              helperText={errors.blQty ? errors.blQty.message : ""}
            />
            <Typography
              variant="body1"
              sx={{ marginBottom: 0, width: "25%", paddingY: "8px" }}
            >
              P {blSubTotal}
            </Typography>
          </div>
          <TextField
            label="Cash Amount"
            fullWidth
            variant="outlined"
            size="small"
            type="number"
            {...register("cs", {
              required: "Cash amount is required",
              valueAsNumber: true,
            })}
            onChange={(e) => {
              setValue("cs", sanitizeNumberInput(e.target.value));
            }}
            onKeyDown={(e) => {
              if (
                e.key === "e" ||
                e.key === "+" ||
                e.key === "-" ||
                e.key === "."
              ) {
                e.preventDefault();
              }
            }}
            value={watch("cs") || ""}
            error={!!errors.cs}
            helperText={errors.cs ? errors.cs.message : ""}
          />
          <Typography variant="h4">
            Total Price: P {totalPrice.toFixed(2)}
          </Typography>
          <Typography variant="h6">
            Change: P {change >= 0 ? change.toFixed(2) : "0.00"}
          </Typography>
          <Box marginTop={3} textAlign="right">
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isButtonDisabled}
            >
              Save & Print
            </Button>
          </Box>
        </Stack>
      </form>
      <Dialog
        open={registerDialogOpen}
        onClose={handleCloseRegisterDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        {registerDialog}
      </Dialog>
      <Snackbar
        open={openSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={5000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert
          severity={
            snackbarMessage.includes("successfully") ? "success" : "error"
          }
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default TransactionForm;
